import React, { useState } from 'react';
import { proposalComparisonService } from '../../../services/api';
import guestSessionService from '../../../services/guestSessionService';
import styles from './GuestMigrationPopup.module.css';

const GuestMigrationPopup = ({ guestSessions, userId, onClose, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState(
    guestSessions.reduce((acc, session) => {
      acc[session.id] = true; // All selected by default
      return acc;
    }, {})
  );

  const handleSessionToggle = (sessionId) => {
    setSelectedSessions(prev => ({
      ...prev,
      [sessionId]: !prev[sessionId]
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(selectedSessions).every(selected => selected);
    const newState = guestSessions.reduce((acc, session) => {
      acc[session.id] = !allSelected;
      return acc;
    }, {});
    setSelectedSessions(newState);
  };

  const handleImportSelected = async () => {
    setLoading(true);
    try {
      const sessionsToImport = guestSessions.filter(session => selectedSessions[session.id]);
      
      if (sessionsToImport.length === 0) {
        console.warn('No sessions selected for import');
        onComplete('error', 0);
        return;
      }
      
      // Extract comparison IDs from selected sessions
      const comparisonIds = sessionsToImport.map(session => session.comparisonId).filter(id => id);
      
      if (comparisonIds.length === 0) {
        console.warn('No valid comparison IDs found in selected sessions');
        onComplete('error', 0);
        return;
      }
      
      console.log(`Migrating specific comparisons: [${comparisonIds.join(', ')}]`);
      
      // Use the new specific migration endpoint
      const response = await proposalComparisonService.migrateSpecificComparisons(comparisonIds, userId);
      const migratedCount = response.data?.data?.migratedComparisons || 0;
      
      console.log(`Migration response:`, response.data);
      console.log(`Successfully processed ${comparisonIds.length} comparisons, ${migratedCount} migrated/assigned`);
      
      // Remove the imported sessions from local storage
      for (const session of sessionsToImport) {
        guestSessionService.deleteGuestSession(session.id);
        console.log(`Removed frontend session: ${session.id}`);
      }
      
      // Clean up guest session data if all sessions are imported
      const remainingSessions = guestSessions.filter(session => !selectedSessions[session.id]);
      if (remainingSessions.length === 0) {
        localStorage.removeItem('guestSessionId');
        sessionStorage.removeItem('guestSessionId');
        localStorage.removeItem('guestSessionCreated');
      }
      
      // Provide feedback based on migration results
      if (migratedCount > 0) {
        onComplete('imported', migratedCount);
      } else if (comparisonIds.length > 0) {
        // Some comparisons were processed but not migrated (already owned, etc.)
        onComplete('imported', sessionsToImport.length);
      } else {
        onComplete('error', 0);
      }
    } catch (error) {
      console.error('Error importing sessions:', error);
      // Still call onComplete to close the popup
      onComplete('error', 0);
    } finally {
      setLoading(false);
    }
  };

  const handleImportAll = async () => {
    // Select all sessions and import
    const allSelected = guestSessions.reduce((acc, session) => {
      acc[session.id] = true;
      return acc;
    }, {});
    setSelectedSessions(allSelected);
    await handleImportSelected();
  };

  const handleDeleteSelected = async () => {
    setLoading(true);
    try {
      const sessionsToDelete = guestSessions.filter(session => selectedSessions[session.id]);
      
      for (const session of sessionsToDelete) {
        guestSessionService.deleteGuestSession(session.id);
      }
      
      // Clean up guest session data if all sessions are deleted
      const remainingSessions = guestSessions.filter(session => !selectedSessions[session.id]);
      if (remainingSessions.length === 0) {
        localStorage.removeItem('guestSessionId');
        sessionStorage.removeItem('guestSessionId');
        localStorage.removeItem('guestSessionCreated');
      }
      
      onComplete('deleted', sessionsToDelete.length);
    } catch (error) {
      console.error('Error deleting sessions:', error);
      onComplete('error', 0);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    // Select all sessions and delete
    const allSelected = guestSessions.reduce((acc, session) => {
      acc[session.id] = true;
      return acc;
    }, {});
    setSelectedSessions(allSelected);
    await handleDeleteSelected();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  const selectedCount = Object.values(selectedSessions).filter(Boolean).length;
  const allSelected = selectedCount === guestSessions.length;

  return (
    <div className={styles.overlay}>
      <div className={styles.popup}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerIcon}>
              <i className="fas fa-download"></i>
            </div>
            <div>
              <h2>Import Your Guest Projects</h2>
              <p>You have {guestSessions.length} project{guestSessions.length !== 1 ? 's' : ''} from your guest sessions. Would you like to import them to your account?</p>
            </div>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.projectsList}>
            <div className={styles.listHeader}>
              <div className={styles.selectAllContainer}>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />
                  <span className={styles.checkmark}></span>
                  Select All ({guestSessions.length} projects)
                </label>
              </div>
              <div className={styles.selectedCount}>
                {selectedCount} selected
              </div>
            </div>

            <div className={styles.projectsGrid}>
              {guestSessions.map((session) => (
                <div 
                  key={session.id} 
                  className={`${styles.projectCard} ${selectedSessions[session.id] ? styles.selected : ''}`}
                  onClick={() => handleSessionToggle(session.id)}
                >
                  <div className={styles.cardHeader}>
                    <div className={styles.checkbox}>
                      <input
                        type="checkbox"
                        checked={selectedSessions[session.id] || false}
                        onChange={() => {}} // Handled by card click
                        onClick={(e) => e.stopPropagation()} // Prevent double toggle
                      />
                      <span className={styles.checkmark}></span>
                    </div>
                    <div className={styles.projectIcon}>
                      <i className="fas fa-file-contract"></i>
                    </div>
                  </div>
                  
                  <div className={styles.cardContent}>
                    <h3>{session.projectName}</h3>
                    <p className={styles.projectType}>{session.projectType}</p>
                    <div className={styles.projectMeta}>
                      <div className={styles.metaItem}>
                        <i className="fas fa-users"></i>
                        <span>
                          {session.contractorCount || (session.data?.contractors || []).length || 0} contractor{(session.contractorCount || (session.data?.contractors || []).length || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className={styles.metaItem}>
                        <i className="fas fa-clock"></i>
                        <span>{formatDate(session.createdAt)}</span>
                      </div>
                      {session.data?.project_budget && (
                        <div className={styles.metaItem}>
                          <i className="fas fa-dollar-sign"></i>
                          <span>${session.data.project_budget}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <div className={styles.actionButtons}>
            <div className={styles.primaryActions}>
              <button 
                className={styles.importButton}
                onClick={handleImportSelected}
                disabled={loading || selectedCount === 0}
              >
                {loading ? (
                  <>
                    <div className={styles.spinner}></div>
                    <span>Importing...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-download"></i>
                    <span>Import Selected ({selectedCount})</span>
                  </>
                )}
              </button>
              
              <button 
                className={styles.importAllButton}
                onClick={handleImportAll}
                disabled={loading}
              >
                <i className="fas fa-download"></i>
                <span>Import All</span>
              </button>
            </div>

            <div className={styles.secondaryActions}>
              <button 
                className={styles.deleteButton}
                onClick={handleDeleteSelected}
                disabled={loading || selectedCount === 0}
              >
                <i className="fas fa-trash"></i>
                <span>Delete Selected</span>
              </button>
              
              <button 
                className={styles.deleteAllButton}
                onClick={handleDeleteAll}
                disabled={loading}
              >
                <i className="fas fa-trash-alt"></i>
                <span>Delete All</span>
              </button>
            </div>
          </div>

          <div className={styles.footerNote}>
            <i className="fas fa-info-circle"></i>
            <span>Imported projects will be available in your dashboard. Deleted projects cannot be recovered.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestMigrationPopup; 